import { zodResolver } from "@hookform/resolvers/zod"
import { Chip, Dialog, DialogActions, DialogContent, Typography } from "@mui/material"
import type { FC } from "react"
import { useMemo, useState } from "react"
import { FormProvider, useFieldArray, useForm, useWatch } from "react-hook-form"

import { DESTINATION_DEFAULT_FORM_VALUES } from "../../../../constants"
import { useGetInventoryShelvingOptions } from "../../../../hooks"
import { useTransferPalletSelection } from "../../hooks"
import { type TransferPalletSelectionFormValues, transferPalletSelectionFormSchema } from "../../schemas"
import type { TransferFormPallet, TransferFormProductInventory } from "../../types"
import { getSelectedShelvingIds } from "../../utils"

import { TransferPalletDestinations } from "./TransferPalletDestinations"
import { TransferPalletSelection } from "./TransferPalletSelection"

import { Button } from "@/components/common/Button"
import CancelDialog from "@/components/common/Modals/CancelDialog"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import type { InventoryPalletGroup, WarehouseArea } from "@/graphql/codegen/graphql"
import { formatNumber } from "@/utils/number"
import { formatWarehouseLabel } from "@/utils/warehouse"

interface TransferPalletSelectionDialogProps {
  inventories: TransferFormProductInventory[]
  productId: number
  warehouseArea: WarehouseArea
  onClose: () => void
  onSubmit: (pallets: TransferFormPallet[]) => void
}

export const TransferPalletSelectionDialog: FC<TransferPalletSelectionDialogProps> = ({
  inventories,
  productId,
  warehouseArea,
  onClose,
  onSubmit,
}) => {
  const inventory = inventories.find((item) => item.product.productId === productId)!

  const [selectedPallets, setSelectedPallets] = useState<InventoryPalletGroup[]>(inventory.pallets)
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false)

  const { product, warehouses } = inventory
  const uomName = product.packageUsWeightUomDetails?.name ?? ""
  const warehouseInventory = warehouses.find((warehouse) => warehouse.warehouseId === warehouseArea.warehouseAreaId)
  const warehouseLabel = warehouseInventory
    ? `${formatWarehouseLabel(warehouseArea)} (${warehouseInventory.numPallets} pallets / ${formatNumber(warehouseInventory.qtyOnHand)} on hand)`
    : formatWarehouseLabel(warehouseArea)

  const form = useForm<TransferPalletSelectionFormValues>({
    resolver: zodResolver(transferPalletSelectionFormSchema),
    defaultValues: { destinations: inventory.pallets.map(({ destination }) => destination) },
    mode: "onChange",
  })

  const {
    control,
    formState: { isValid, isDirty },
  } = form

  const {
    fields: destinationFields,
    append: appendDestination,
    remove: removeDestination,
  } = useFieldArray({ control, name: "destinations" })

  const destinations = useWatch({ control, name: "destinations" })
  const selectedReferenceNbrs = useMemo(
    () => new Set(selectedPallets.map((pallet) => pallet.referenceNbr)),
    [selectedPallets]
  )
  const selectedShelvingIds = useMemo(
    () => [
      ...getSelectedShelvingIds({ inventories, productId }),
      ...destinations
        .map((destination) => destination.shelving.warehouseShelvingId)
        .filter((id): id is string => id !== null),
    ],
    [inventories, productId, destinations]
  )

  const {
    palletGroups,
    isFetching,
    sortBy,
    sortDirection,
    aisleCodes,
    handleSearchChange,
    handleSortChange,
    handleAisleCodesChange,
  } = useTransferPalletSelection({
    productExternalSystemId: product.externalSystemId ?? "",
    warehouseAreaExternalSystemId: warehouseArea.externalSystemId ?? "",
  })

  const { aisleOptions } = useGetInventoryShelvingOptions({
    warehouseAreaId: warehouseArea.warehouseAreaId,
    includeUnavailable: true,
  })

  const handlePalletToggle = (pallet: InventoryPalletGroup) => {
    const palletIndex = selectedPallets.findIndex((selected) => selected.referenceNbr === pallet.referenceNbr)

    if (palletIndex === -1) {
      appendDestination(DESTINATION_DEFAULT_FORM_VALUES)
      setSelectedPallets((prev) => [...prev, pallet])
    } else {
      removeDestination(palletIndex)
      setSelectedPallets((prev) => prev.filter((_, index) => index !== palletIndex))
    }
  }

  const handlePalletRemove = (palletIndex: number) => {
    removeDestination(palletIndex)
    setSelectedPallets((prev) => prev.filter((_, index) => index !== palletIndex))
  }

  const handleCancel = () => {
    if (isDirty) {
      setIsCancelConfirmationOpen(true)
    } else {
      onClose()
    }
  }

  const handleSubmit = form.handleSubmit((formValues) => {
    const pallets = formValues.destinations.map((destination, index) => ({ ...selectedPallets[index], destination }))
    onSubmit(pallets)
  })

  if (isCancelConfirmationOpen) {
    return <CancelDialog onClose={() => setIsCancelConfirmationOpen(false)} onConfirm={onClose} />
  }

  return (
    <Dialog
      open
      fullWidth
      maxWidth={false}
      PaperProps={{ className: "h-full" }}
      onClose={handleCancel}
      data-testid='transfer.pallet-selection-dialog'
    >
      <DialogHeader title='Select Pallets' onClose={handleCancel} />
      <div className='flex items-center gap-x-6 border-b border-gray-400 bg-gray-100 px-10 py-3'>
        <div className='flex items-center gap-x-2'>
          <Typography variant='body2' className='shrink-0 text-gray-700'>
            Product:
          </Typography>
          <Chip
            label={product.companyProductDescription}
            size='small'
            className='rounded-sm bg-gray-300'
            data-testid='transfer.pallet-selection-dialog.product'
          />
        </div>
        <div className='flex items-center gap-x-2'>
          <Typography variant='body2' className='shrink-0 text-gray-700'>
            Warehouse:
          </Typography>
          <Chip
            label={warehouseLabel}
            size='small'
            className='rounded-sm bg-gray-300'
            data-testid='transfer.pallet-selection-dialog.warehouse'
          />
        </div>
      </div>
      <FormProvider {...form}>
        <DialogContent className='flex min-h-0 flex-1 flex-col gap-6 overflow-hidden px-10 pb-10 pt-6'>
          <TransferPalletSelection
            palletGroups={palletGroups}
            isFetching={isFetching}
            selectedReferenceNbrs={selectedReferenceNbrs}
            sortBy={sortBy}
            sortDirection={sortDirection}
            aisleCodes={aisleCodes}
            aisleOptions={aisleOptions}
            uomName={uomName}
            onPalletToggle={handlePalletToggle}
            onSortChange={handleSortChange}
            onSearchChange={handleSearchChange}
            onAisleCodesChange={handleAisleCodesChange}
          />
          <TransferPalletDestinations
            warehouseArea={warehouseArea}
            destinationFields={destinationFields}
            selectedPallets={selectedPallets}
            selectedShelvingIds={selectedShelvingIds}
            uomName={uomName}
            onPalletRemove={handlePalletRemove}
          />
        </DialogContent>
      </FormProvider>
      <DialogActions className='justify-between px-10 pb-10'>
        <Button
          appearance='outlined'
          onClick={handleCancel}
          data-testid='transfer.pallet-selection-dialog.cancel-button'
        >
          Cancel
        </Button>
        <Button
          appearance='contained'
          disabled={!isValid}
          onClick={handleSubmit}
          data-testid='transfer.pallet-selection-dialog.submit-button'
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  )
}
